import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X, Check } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();

function buildImageSearchQuery(product) {
  if (!product) return '';
  const name = (product.name || '').trim();
  const oem = (product.oem || '').trim();
  return [name, oem].filter(Boolean).join(' ');
}

export default function ImageSearchModal({ product, token, onClose, onApplied }) {
  const [query, setQuery] = useState(() => buildImageSearchQuery(product));
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  // Дедупликация по URL
  const dedupedResults = useMemo(() => {
    const seen = new Set();
    return results.filter((r) => {
      if (seen.has(r.url)) return false;
      seen.add(r.url);
      return true;
    });
  }, [results]);

  const handleSearch = useCallback(async (overrideQuery) => {
    const searchQuery = (overrideQuery ?? query).trim();
    if (!searchQuery || !product) return;
    setLoading(true);
    setError('');
    setResults([]);
    setSelectedUrls(new Set());
    try {
      const resp = await fetch(
        `${API}/products/${product.id}/search-images?query=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('Ошибка поиска');
      const data = await resp.json();
      setResults(data.results || []);
      if (!data.results?.length) setError('Картинки не найдены. Попробуйте другой запрос.');
    } catch (e) {
      setError('Не удалось выполнить поиск. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  }, [query, product, token]);

  const toggleSelect = useCallback((url) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  }, []);

  const handleApply = useCallback(async () => {
    if (!selectedUrls.size || !product) return;
    setApplying(true);
    setError('');
    try {
      const urls = Array.from(selectedUrls);
      const resp = await fetch(`${API}/products/${product.id}/apply-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ urls }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Ошибка применения картинки');
      }
      const data = await resp.json();
      onApplied(product.id, data);
      onClose();
    } catch (e) {
      setError(e.message || 'Не удалось применить картинку.');
    } finally {
      setApplying(false);
    }
  }, [selectedUrls, product, token, onApplied, onClose]);

  useEffect(() => {
    if (!product) return;
    const nextQuery = buildImageSearchQuery(product);
    setQuery(nextQuery);
    setResults([]);
    setSelectedUrls(new Set());
    setError('');
    if (nextQuery.trim()) {
      handleSearch(nextQuery);
    }
  }, [product?.id]); // eslint-disable-line

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  if (!product) return null;

  const count = selectedUrls.size;

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Выбрать картинку для «{product.name}»</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mt-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Поисковый запрос..."
            className="flex-1"
          />
          <Button onClick={() => handleSearch()} disabled={loading || !query.trim()}>
            <Search className="w-4 h-4 mr-1" />
            {loading ? 'Поиск...' : 'Найти'}
          </Button>
        </div>

        {error && (
          <div className="text-sm text-red-600 mt-1">{error}</div>
        )}

        {dedupedResults.length > 0 && (
          <div className="overflow-y-auto flex-1 mt-3">
            <div className="grid grid-cols-4 gap-2">
              {dedupedResults.map((r) => {
                const isSelected = selectedUrls.has(r.thumb);
                return (
                  <button
                    key={r.thumb}
                    onClick={() => toggleSelect(r.thumb)}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all aspect-square bg-gray-100 ${
                      isSelected
                        ? 'border-indigo-500 ring-2 ring-indigo-300'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={r.thumb}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-indigo-500/20 flex items-center justify-center">
                        <Check className="w-8 h-8 text-indigo-600 bg-white rounded-full p-1 shadow" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {dedupedResults.length === 0 && !loading && !error && (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            Введите запрос и нажмите «Найти»
          </div>
        )}

        <div className="flex justify-between items-center mt-3 pt-3 border-t">
          <div className="text-sm text-gray-500">
            {count > 0 ? `Выбрано: ${count} ${count === 1 ? 'картинка' : count < 5 ? 'картинки' : 'картинок'}` : 'Кликните для выбора'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={applying}>
              <X className="w-4 h-4 mr-1" />
              Отмена
            </Button>
            <Button onClick={handleApply} disabled={count === 0 || applying}>
              <Check className="w-4 h-4 mr-1" />
              {applying ? 'Сохраняю...' : count > 1 ? `Добавить ${count} в карусель` : 'Применить'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
